import {
    OFFER_ID_TRIAL,
    OFFER_SUITE_TRIAL,
    PATH_PLANS,
    PATH_TRIAL_SIGNUP_SUCCESS,
    RECAPTCHA_ACTION,
    TRIAL_PRODUCT_DESC,
    TRIAL_PRODUCT_GUID,
    SELECTED_NUMBER_TYPE,
    LMI_LOGIN_FLOW_ENABLED
} from '~/common/utils/consts.js';
// Import Dependencies
import React, { Component, Fragment } from 'react';
import { createPersonalizedPurchaseEvent, gtm, scBeacon, startTrialFlowEvent, completeTrialFlow, loadFormEvent, startFormEvent, abandonFormEvent, submitFormEvent, continueFormEvent, errorFormEvent, clickCtaEvent, viewContentEvent } from '~/common/utils/tracking.js';
import { inject, observer } from 'mobx-react';
import * as Sentry from '@sentry/react';

import CommonButton from '~/common/components/CommonButton';
import FingerPrint from '~/common/components/FingerPrint';
import InputField from '~/common/components/InputField';
import LoadingSpinner from '~/common/components/LoadingSpinner';
import NewsletterCheckbox from '~/common/components/NewsletterCheckbox';
import ReCaptchaAttribution from '~/common/components/ReCaptchaAttribution';
import Script from 'react-load-script';
import deepGet from '~/common/utils/deepGet.js';
import each from 'lodash/each';
import isPlainObject from 'lodash/isPlainObject';
import { verifyReCaptcha } from '~/common/services/reCaptcha';
import ConvertStringToReact from '~/common/components/ConvertStringToReact';
// Import images
import warning from '~/common/images/warning.svg';
import checkIcon from '~/common/images/gh-green-check.svg';
import { withRouter } from 'react-router-dom';


// Import Local Styles
import styles from './trial-signup-form.css';

const START_FREE_TRIAL = 'Start my free trial';
const FIELDS_LMI_FIRST_STEP = ['Email'];
const FIELDS_GH_FIRST_STEP = ['FirstName', 'LastName', 'Email', 'Password', 'ContactPhone'];
const FIELDS_SECOND_STEP = ['Pin'];
const FIELDS = [...FIELDS_LMI_FIRST_STEP, ...FIELDS_GH_FIRST_STEP, ...FIELDS_SECOND_STEP];
@withRouter
@inject(
    'emailCaptureStore',
    'trialSignupStore',
    'lockNumberStore',
    'sharedFormUtilsStore',
    'transferNumberStore',
    'numberLockSessionStore',
    'formsStore',
    'userLocationStore',
    'sharedFormUtilsStore',
    'flexFrameStore',
    'productTrackingStore',
    'modalStore',
    'reCaptchaStore'
)
@observer
export default class TrialSignupForm extends Component {
    state = {
        formInteracted: false,
        trialSignupSuccess: false
    };

    componentDidMount() {
        const { sharedFormUtilsStore, formsStore, convertToPaidStore, lockNumberStore } = this.props;

        gtm(viewContentEvent('trial flow', 'register', SELECTED_NUMBER_TYPE[lockNumberStore.selectedNumberType]));

        // Set form type
        formsStore.setFormType('trial-form');
        gtm(loadFormEvent('trial-form'));
        // if email is already used error is true
        if (!sharedFormUtilsStore.emailAlreadyUsedError) return;

        // clear error
        sharedFormUtilsStore.setEmailAlreadyUsedError(false);
        // reset all fields to empty and remove errors if any
        for (const field of FIELDS) {
            formsStore.setFormData(field, '');
            formsStore.clearFieldError(field);
        }

        // if email was saved from another form (i.e. buy form) validate it on page entry
        if (!convertToPaidStore?.isUserConvertingToPaid && formsStore?.Email) this.validateEmail();
    }

    // autoEmailValidation

    componentWillUnmount() {
        if (this.state.formInteracted && !this.state.trialSignupSuccess) {
            gtm(abandonFormEvent('trial-form'));
        }
    }

    getFields() {
        const { formsStore } = this.props;
        return formsStore.UserKey ? [...FIELDS_LMI_FIRST_STEP, ...FIELDS_SECOND_STEP] : [...FIELDS_GH_FIRST_STEP, ...FIELDS_SECOND_STEP];
    }

    getFirstStepFields() {
        const { formsStore } = this.props;
        return formsStore.UserKey ? FIELDS_LMI_FIRST_STEP : FIELDS_GH_FIRST_STEP;
    }

    autoFillValidateEmail = (ev) => {
        const { formsStore } = this.props;
        // checks if the input field has errors and that it is not focused, therefor aoutofilled
        if (formsStore.DoesEmailHaveErrors || document.activeElement === ev.target) return;
        this.validateEmail();
    };

    validateFirstStep(e) {
        e.preventDefault();
        const { formsStore, trialSignupStore, reCaptchaStore } = this.props;
        // run validation on all fields
        const fields = this.getFirstStepFields();
        formsStore.validateForm(fields, true);
        // check if any error exist from validation
        if (formsStore.doesFormHaveErrors(fields)) return false;

        // https://developers.google.com/recaptcha/docs/v3#programmatically_invoke_the_challenge
        // eslint-disable-next-line no-undef
        return grecaptcha.ready(() => {
            // eslint-disable-next-line no-undef
            grecaptcha.execute(reCaptchaStore.siteKey, { action: RECAPTCHA_ACTION.GH_TRIAL_START }).then((token) => {
                verifyReCaptcha(token).then((response) => {
                    // See data shape here: https://developers.google.com/recaptcha/docs/v3#site_verify_response
                    const data = response.data;

                    // Score range is 0.0 to 1.0.
                    // 0.5 is Google's recommended default threshold
                    // Refer to https://developers.google.com/recaptcha/docs/v3#interpreting_the_score
                    trialSignupStore.setTrialSignupError('');
                    if (data.action !== RECAPTCHA_ACTION.GH_TRIAL_START || data.score < reCaptchaStore.scoreThreshold) {
                        trialSignupStore.setTrialSignupError('Something went wrong. Please visit <a style="color:#3E94CB;font-weight: 600;" href="https://support.grasshopper.com/help/why-am-i-receiving-an-error-while-signing-up" target="_blank">support</a> or try again.');
                    } else {
                        gtm(continueFormEvent('trial-form', 1));
                        trialSignupStore.setFormStep(2);
                        this.sendPin();
                    }
                });
            });
        });
    }

    validateEmail = () => {
        const { sharedFormUtilsStore, formsStore, emailCaptureStore } = this.props;

        // If there is a UserKey, then the user already signed into LMI with this email.
        // Note: If the user changes the email, then formsStore clears out the UserKey.
        // See setFormData() in /client/common/stores/ui/forms.js
        if (formsStore.UserKey) return undefined;

        // no error, validate the email and move form to step 2 on success
        return sharedFormUtilsStore.validateEmail(
            formsStore.LowerCaseDomainEmail,
            (data) => {
                // if email is already in use
                if (!data.IsAvailable) {
                    gtm(errorFormEvent('trial-form', 'Email', 'Looks like you have already used your free trial'));
                    return sharedFormUtilsStore.setEmailAlreadyUsedError(true);
                }

                // if email can authenticate in LMI Identity
                if (data.CanUserAuthenticateInLmiIdentity) {
                    this.observeLoginModal();
                    return undefined;
                }

                // if email is disposable email
                if (data.IsDisposableEmail) {
                    gtm(errorFormEvent('trial-form', 'Email', 'Please provide a valid email address.'));
                    return formsStore.setFieldError('Email', 'Please provide a valid email address.');
                }

                emailCaptureStore.addCapturedEmail(data.UserName === formsStore.Email ? formsStore.Email : data.UserName, true);
                return undefined;
            }
        );
    };

    observeLoginModal = () => {
        const modalFeatureType = LMI_LOGIN_FLOW_ENABLED ? 'loginLmiModal' : 'loginModal';
        if (modalFeatureType) {
            this.triggerLoginModal(modalFeatureType);
        }
    };

    triggerLoginModal = (modalFeatureType) => {
        const { modalStore } = this.props;
        window.scroll({ top: 0, left: 0, behavior: 'smooth' });
        modalStore.setModalOptions({
            childComponentString: modalFeatureType,
            isModalClosable: false,
            modalType: 'xlarge',
            isModalOpen: true
        });
    };

    sendPin(callUser = false) {
        const { formsStore, trialSignupStore, numberLockSessionStore } = this.props;
        // clear any existing field error for pin
        formsStore.clearFieldError('Pin');
        // check if any error exist from validation
        if (formsStore.doesFormHaveErrors(['ContactPhone'])) return false;

        // if no error, clear error and send user pin
        trialSignupStore.setValidatePhoneNumberError('');

        return trialSignupStore.sendUserPin(
            numberLockSessionStore.NLS,
            formsStore.ContactPhone,
            callUser,
            err => this.processUserPinError(err)
        );
    }

    processUserPinError(err) {
        const { formsStore, trialSignupStore } = this.props;
        if (isPlainObject(err) && err.ErrorCode && err.Field) {
            // Invalid session ID
            if (err.ErrorCode === 2003 && err.Field === 'numberLockSessionId') {
                gtm(errorFormEvent('trial-signup', 'NumberSession', 'Something went wrong. Please try again.'));
                // show generic error, not something the user can fix
                trialSignupStore.setValidatePhoneNumberError('Something went wrong. Please try again.');
                return err;
            }
            // Invalid Phone Number
            if (err.ErrorCode === 2003 && err.Message === 'InvalidContactPhone') {
                formsStore.setFieldError('ContactPhone', 'Please enter a valid phone number.');
                return err;
            }
            // Phone number associated with another account
            if (err.ErrorCode === 3007 && err.Field === 'contactPhone') {
                formsStore.setFieldError('ContactPhone', 'The number you entered has already been used to trial Grasshopper.');
                return err;
            }
        }

        gtm(errorFormEvent('trial-signup', 'Pin', 'Something went wrong. Please try again.'));

        // When the error is not handled, log it
        trialSignupStore.setValidatePhoneNumberError('Something went wrong. Please try again.');
        Sentry.captureMessage('trialSignupError', {
            extra: {
                errorObj: err
            }
        });

        return err;
    }

    resendPin(callUser) {
        // clear form error for wrong pin
        this.props.trialSignupStore.setWrongPinError(false);
        this.sendPin(callUser);
    }

    signupTrialObject() {
        const { formsStore, lockNumberStore, userLocationStore, transferNumberStore, flexFrameStore, productTrackingStore } = this.props;
        const { deviceType, deviceID, isRubySignup } = productTrackingStore;

        const offerSuite = OFFER_SUITE_TRIAL;
        const offerId = OFFER_ID_TRIAL;

        const signupObject = {
            LmiUserKey: formsStore.UserKey ? formsStore.UserKey : undefined,
            CustomerInfo: {
                FirstName: formsStore.FirstName ? formsStore.FirstName : undefined,
                LastName: formsStore.LastName ? formsStore.LastName : undefined,
                ContactPhone: formsStore.parsedContactNumber ? formsStore.parsedContactNumber : undefined,
                Email: formsStore.Email ? formsStore.Email : undefined,
                Password: formsStore.Password ? formsStore.Password : undefined
            },
            PromoCode: null,
            PromoId: null,
            ClientIpAddress: userLocationStore.ipAddress,
            IsNewsletter: formsStore.IsNewsletter,
            IsTransferNeeded: transferNumberStore.transferNumber,
            PartnerId: null,
            OfferId: offerId,
            OfferSuiteId: offerSuite,
            OptInSms: true,
            SubscriptionNumber: lockNumberStore.selectedNumberE164,
            ProductGuid: TRIAL_PRODUCT_GUID,
            ProductDesc: TRIAL_PRODUCT_DESC,
            Pin: formsStore.parsedPin,
            DeviceFingerprintID: flexFrameStore.FingerPrintID
        };
        // add product tracking params if they are present
        if (deviceType !== '') signupObject.deviceType = deviceType;
        if (deviceID !== '') signupObject.deviceID = deviceID;
        if (isRubySignup) signupObject.IsRubyAffiliateSignUp = isRubySignup;

        return signupObject;
    }

    signupTrial() {
        const { lockNumberStore, formsStore, trialSignupStore, numberLockSessionStore } = this.props;

        // make sure a number is selected
        if (!(lockNumberStore.selectedNumberE164 && lockNumberStore.numberIsLocked)) {
            return trialSignupStore.setTrialSignupError('Please re-select a number and try again.');
        }
        // run validation on all fields
        const fields = this.getFields();
        formsStore.validateForm(fields, true);

        // if errors are fields don't continue
        if (formsStore.doesFormHaveErrors([...this.getFirstStepFields(), FIELDS_SECOND_STEP])) {
            return false;
        }

        // no errors found, send a request to signup the user
        return trialSignupStore.signupTrial(
            numberLockSessionStore.NLS,
            this.signupTrialObject(),
            successObj => this.processSignupSuccess(successObj),
            err => this.processSignupError(err)
        );
    }

    processSignupSuccess(successObj) {
        if (!isPlainObject(successObj)) throw new Error('invalid data response');

        const { emailCaptureStore, history, formsStore, productTrackingStore } = this.props;
        // alert the emailCapture mechanism of the successful email signup before anything else
        emailCaptureStore.setSubmittedEmail(formsStore.Email);
        // alert marketing if this was a ruby referal
        if (productTrackingStore.isRubySignup) productTrackingStore.sendIsRubySignup(formsStore.Email);

        this.setState({ trialSignupSuccess: true });

        gtm(continueFormEvent('trial-form', 2));
        gtm(submitFormEvent('trial-form'));

        // ToDo :: Old track conversion. Will remove once confirmed by GA after new updates
        // gtm(createTrialSignupSuccessEvent(successObj));

        // track SCBeacon: successful trial signup
        scBeacon(createPersonalizedPurchaseEvent('3'));
        // complete trial flow gtm event
        gtm(completeTrialFlow(formsStore.Email));
        // add success redirect here;
        history.push(PATH_TRIAL_SIGNUP_SUCCESS);
    }

    processSignupError(err) {
        const { formsStore, trialSignupStore, sharedFormUtilsStore } = this.props;

        const errorData = deepGet(err, ['response', 'data', 'Details', 0]) || err;

        // handle error validation for specific fields
        if (isPlainObject(err.response.data) && err.response.data.ErrorType === 'ModelValidationError') {
            const modelValidationErrors = [];

            // create an array of 2003 and 2004 form errors
            each(err.response.data.Details, (detail) => {
                if (
                    [2003, 2004].includes(detail.ErrorCode) ||
                    (detail.ErrorCode === 0 && detail.Field === 'signupData.CustomerInfo.Email')
                ) {
                    modelValidationErrors.push({
                        field: detail.Field.split('.').pop(),
                        error: detail.Cause
                    });
                }
            });

            // loop through error object and set errors on each field
            for (const error of modelValidationErrors) {
                formsStore.setFieldError(error.field, error.error);
            }

            // set message in billing error box
            trialSignupStore.setTrialSignupError('Please correct any errors below and try submitting your information again.');
            return false;
        }

        if (errorData.ErrorCode && errorData.AdditionalMessage) {
            // email is already in use
            if (errorData.ErrorCode === 5005 && errorData.AdditionalMessage.includes('We love repeat customers!')) {
                gtm(errorFormEvent('trial-form', 'Email', 'The email address you entered has already been used to trial Grasshopper.'));
                formsStore.setFieldError('Email', 'The email address you entered has already been used to trial Grasshopper.');
                return false;
            }
            // Password is not a valid password
            if (errorData.ErrorCode === 5005 && errorData.AdditionalMessage === 'Password is not a valid password.') {
                gtm(errorFormEvent('trial-form', 'Your password must be at least 8 characters, and use only letters, numbers, @, $, or ?.'));
                formsStore.setFieldError('Password', 'Your password must be at least 8 characters, and use only letters, numbers, @, $, or ?.');
                return false;
            }
        }

        if (errorData.ErrorCode && errorData.Field) {
            // Phone already in used
            if (errorData.ErrorCode === 3007 && errorData.Field === 'PhoneNumberInUse') {
                gtm(errorFormEvent('trial-form', 'UserName', 'Looks like you have already used your free trial'));
                sharedFormUtilsStore.setEmailAlreadyUsedError(true);
                return false;
            }

            // Invalid Pin Number
            if (errorData.ErrorCode === 3008 && errorData.Field === 'InvalidPin') {
                gtm(errorFormEvent('trial-form', 'Pin', 'Hmm… that’s not the right code. Try again or contact support.'));
                trialSignupStore.setWrongPinError(true);
                return false;
            }

            // number is no longer in session
            if (errorData.ErrorCode === 5006 || errorData.ErrorCode === 5007) {
                gtm(errorFormEvent('trial-form', 'form', 'We’re sorry, the page has timed-out. Please try again.'));
                trialSignupStore.setTrialSignupError('We’re sorry, the page has timed-out. Please try again.');
                return false;
            }

            // subscription number not tied to session or general core error
            if (errorData.ErrorCode === 5999) {
                gtm(errorFormEvent('trial-form', 'form', 'Something went wrong. Please try again.'));
                // show generic error, not something the user can fix
                trialSignupStore.setTrialSignupError('Something went wrong. Please try again.');
            }
        }

        gtm(errorFormEvent('trial-form', 'form', 'Something went wrong. Please try again.'));

        // When the error is not handled, log it
        trialSignupStore.setTrialSignupError('Something went wrong. Please try again.');
        Sentry.captureMessage('trialSignupError', {
            extra: {
                errorObj: errorData
            }
        });
        return false;
    }

    callStartTrialFormEvent() {
        if (!this.state.formInteracted) {
            this.setState({ formInteracted: true });
            gtm(startTrialFlowEvent());
            gtm(startFormEvent('trial-form'));
        }
    }

    renderForm() {
        const { formsStore, trialSignupStore, reCaptchaStore } = this.props;
        const {
            ContactPhone,
            ContactPhoneError,
            Pin,
            PinError,
            FirstName, FirstNameError,
            LastName, LastNameError,
            Email, EmailError,
            Password, PasswordError, UserKey
        } = formsStore;

        const { isCodeSent, trialSignupError, codeSend } = trialSignupStore;

        // https://developers.google.com/recaptcha/docs/v3#programmatically_invoke_the_challenge
        const reCaptchaScriptUrl = `https://www.google.com/recaptcha/api.js?render=${reCaptchaStore.siteKey}`;


        return (
            <div className={styles['form-wrapper']}>
                <Script url={reCaptchaScriptUrl} />
                <h2 className={styles.header}>Register for your Free Trial</h2>

                {!!trialSignupError && <div className={styles['api-error']}>
                    <ConvertStringToReact htmlString={trialSignupError} />
                </div>
                }

                {/* Email */}
                <InputField
                    allowedCharacters="@.!#$%&'*+-/=?^_`{|}~"
                    autoComplete="email"
                    defaultValue={Email}
                    error={EmailError}
                    id="email"
                    label="Email"
                    name="Email"
                    onBlur={this.validateEmail}
                    onChange={this.autoFillValidateEmail}
                    onFocus={() => this.callStartTrialFormEvent()}
                    restrictCharacters="alphaNumericOnly"
                    toolTip="This email address is the main contact for your account. It will be used to communicate with you regarding billing, system updates, announcements, and other account-related matters"
                    tooltipPositionTouch='top'
                    type="email"
                />

                {/* Password */}
                {!UserKey &&
                    <InputField
                        allowedCharacters="-!@#$%^&*"
                        autoComplete="off"
                        defaultValue={Password}
                        displayHintOnError={true}
                        error={PasswordError}
                        hint={
                            <Fragment>
                                <p>Use between 8 and 29  characters including at least:</p>
                                <p>1 uppercase letter</p>
                                <p>1 number</p>
                                <p>1 valid symbol ! @ # $ - % ^ & *</p>
                                <p>No spaces</p>
                            </Fragment>
                        }
                        id="password"
                        label="Password"
                        maxLength={29}
                        name="Password"
                        onFocus={() => this.callStartTrialFormEvent()}
                        restrictCharacters="alphaNumericOnly"
                        type="password"
                    />
                }
                {/* First Name */}
                <div className={styles['small-input-wrapper']}>
                    <InputField
                        allowedCharacters=" -'"
                        autoComplete='given-name'
                        defaultValue={FirstName}
                        disabled={UserKey}
                        error={FirstNameError}
                        id="first-name"
                        label="First Name"
                        maxLength={50}
                        name="FirstName"
                        onFocus={() => this.callStartTrialFormEvent()}
                        restrictCharacters="alphaNumericOnly"
                        type="text"
                    />
                </div>
                {/* Last Name */}
                <div className={styles['small-input-wrapper']}>
                    <InputField
                        allowedCharacters=" -'"
                        autoComplete="family-name"
                        defaultValue={LastName}
                        disabled={UserKey}
                        error={LastNameError}
                        id="last-name"
                        label="Last Name"
                        maxLength={50}
                        name="LastName"
                        onFocus={() => this.callStartTrialFormEvent()}
                        restrictCharacters="alphaNumericOnly"
                        type="text"
                    />
                </div>

                <h3 className={styles['sub-header']}>Verify your Personal Phone Number</h3>
                <p className={styles.hint}>This is where your Grasshopper calls will forward to. For most people, this is their personal cell phone number. </p>

                {/* Contact Number */}
                <InputField
                    autoComplete="phone"
                    defaultValue={ContactPhone}
                    error={ContactPhoneError}
                    id="contact-number"
                    label="Phone Number"
                    mask={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                    name="ContactPhone"
                    onFocus={() => this.callStartTrialFormEvent()}
                    restrictCharacters="numericOnly"
                    type="tel"
                />
                {!isCodeSent ?
                    <CommonButton
                        data-gaeventaction="step-1"
                        data-gaeventcategory="trial-intent"
                        id="send-code"
                        modifier={styles.button}
                        onClick={e => this.validateFirstStep(e)}
                    >
                        Send Verification Code
                    </CommonButton>
                    :
                    // PIN
                    <div className={styles['pin-wrapper']}>
                        <div className={styles['send-copy-wrapper']}>
                            <h3 className={styles['send-header']}>
                                <img className={styles['check-icon']} src={checkIcon}></img>
                                {codeSend}
                            </h3>
                            <p className={styles['send-copy']}>
                                Don’t see the text? You can&#160;
                                <CommonButton
                                    modifier={styles['resend-button']} onClick={() => this.resendPin(false)}
                                    tertiary
                                >resend by text</CommonButton> or&#160;
                                <CommonButton
                                    modifier={styles['resend-button']} onClick={() => { this.resendPin(true); }}
                                    tertiary
                                >resend by call.</CommonButton>
                            </p>
                        </div>
                        <InputField
                            defaultValue={Pin}
                            error={PinError}
                            id="pin"
                            label="Enter the verification code"
                            name="Pin"
                            onFocus={() => this.callStartTrialFormEvent()}
                            restrictCharacters="numericOnly"
                            type="number"
                        />
                        <CommonButton
                            data-gaeventaction="step-2"
                            data-gaeventcategory="trial-intent"
                            id="start-trial"
                            modifier={styles.button}
                            onClick={() => this.signupTrial()}
                        >{START_FREE_TRIAL}</CommonButton>
                    </div>
                }
                {isCodeSent && <NewsletterCheckbox button={START_FREE_TRIAL} />}
                <ReCaptchaAttribution />
            </div>
        );
    }

    renderBlocker() {
        const { performingTrialSignup } = this.props.trialSignupStore;

        if (performingTrialSignup) {
            return (
                <div className={styles.blocker}>
                    <LoadingSpinner />
                    <p className={styles['blocker-text']}>Verifying...</p>
                </div>
            );
        }

        return null;
    }

    renderEmailAlreadyUsedError() {
        const { emailAlreadyUsedError } = this.props.sharedFormUtilsStore;

        if (emailAlreadyUsedError) {
            return (
                <div className={styles['warning-blocker']} id="trial__email-in-use-blocker">
                    <img alt="warning icon" src={warning} />
                    <p className={styles['warning-text']}>Looks like you have already used your free trial.</p>
                    <CommonButton
                        alternateColor={true}
                        data-gaeventaction="body_buy_already_tried"
                        data-gaeventcategory="purchase-intent"
                        id="email-used-back-to-plans"
                        modifier={`${styles.button} ${styles['button--small']}`}
                        onClick={() => gtm(clickCtaEvent('Purchase Grasshopper', PATH_PLANS, 'trial signup form'))}
                        to={PATH_PLANS}
                    >Purchase Grasshopper</CommonButton>
                </div>
            );
        }
        return null;
    }

    renderWrongPin() {
        const { wrongPinError } = this.props.trialSignupStore;

        if (wrongPinError) {
            return (
                <div className={styles['warning-blocker']} id="trial__wrong-pin-blocker">
                    <img alt="warning icon" src={warning} />
                    <p className={styles['warning-text']}>Hmm… that’s not the right code. Try again or contact support. </p>
                    <CommonButton
                        id="buy-now"
                        modifier={`${styles.button} ${styles['button--small']}`}
                        onClick={() => this.resendPin(false)}
                    >Resend code</CommonButton>
                </div>
            );
        }
        return null;
    }

    render() {
        const { formStep, validatePhoneNumberError } = this.props.trialSignupStore;

        return (
            <div className={styles.container}>
                <div className={styles.stepper}>Step {formStep} of 2</div>
                {validatePhoneNumberError ? <div className={styles['api-error']}>{validatePhoneNumberError}</div> : null}
                {this.renderForm()}
                {this.renderBlocker()}
                {this.renderEmailAlreadyUsedError()}
                {this.renderWrongPin()}
                {/* load device fingerprint */}
                <FingerPrint />
            </div>
        );
    }
}
